import { useLayoutEffect, useRef } from 'react';
import { setCaretPosition } from '../utils/caretPositioning';

/**
 * Hook for managing caret positioning in task text elements
 * @param {Object} params - Configuration object
 * @param {string|null} params.editingTaskId - Currently editing task ID
 * @param {Array} params.tasks - All tasks
 * @param {Object} params.editingElementRef - Ref to the editing element
 */
export function useCaretPositioning({
  editingTaskId,
  tasks,
  editingElementRef
}) {
  // Refs to store caret positioning data for useLayoutEffect
  const clickCaretPositionRef = useRef(null);
  const arrowKeyCaretPositionRef = useRef(null);
  const tabCaretPositionRef = useRef(null);

  // Handle caret positioning after click - runs after React renders
  useLayoutEffect(() => {
    if (clickCaretPositionRef.current === null) return;
    
    const { taskId, targetCharOffset, isPlaceholder } = clickCaretPositionRef.current;
    clickCaretPositionRef.current = null; // Clear after use
    
    const element = document.querySelector(`.task-text[data-react-key="${taskId}"]`);
    if (!element) return;
    
    editingElementRef.current = element;
    
    // Handle placeholder task - keep empty so placeholder shows
    if (isPlaceholder) {
      element.textContent = '';
      element.classList.add('task-text-placeholder-empty');
      element.focus();
      const range = document.createRange();
      range.setStart(element, 0);
      range.collapse(true);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    
    // Ensure element has task text (should be set by useEffect in TaskText)
    const task = tasks.find(t => t._reactKey === taskId);
    if (task && (!element.textContent || element.textContent.trim() === '')) {
      element.textContent = task.text || '';
    }
    
    element.focus();
    
    // Position caret at the calculated character offset
    if (targetCharOffset !== null && targetCharOffset >= 0) {
      const textContent = element.textContent || '';
      const textLength = textContent.length;
      const caretOffset = Math.min(targetCharOffset, textLength);
      
      // Find the text node and position the caret
      const range = document.createRange();
      const selection = window.getSelection();
      
      if (textLength > 0) {
        const walker = document.createTreeWalker(
          element,
          NodeFilter.SHOW_TEXT,
          null
        );
        
        let currentOffset = 0;
        let textNode = walker.nextNode();
        let targetNode = null;
        let targetOffset = 0;
        
        while (textNode) {
          const nodeLength = textNode.textContent.length;
          if (currentOffset + nodeLength >= caretOffset) {
            targetNode = textNode;
            targetOffset = caretOffset - currentOffset;
            break;
          }
          currentOffset += nodeLength;
          textNode = walker.nextNode();
        }
        
        if (targetNode) {
          range.setStart(targetNode, targetOffset);
          range.collapse(true);
        } else {
          // Fallback: position at end
          range.selectNodeContents(element);
          range.collapse(false);
        }
      } else {
        range.setStart(element, 0);
        range.collapse(true);
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
      
    }
  }, [editingTaskId, tasks, editingElementRef]);
  
  // Handle caret positioning after arrow key navigation
  useLayoutEffect(() => {
    if (arrowKeyCaretPositionRef.current === null) return;
    
    const { taskId, targetPosition } = arrowKeyCaretPositionRef.current;
    arrowKeyCaretPositionRef.current = null; // Clear after use
    
    const element = document.querySelector(`.task-text[data-react-key="${taskId}"]`);
    if (!element) return;
    
    setCaretPosition(element, targetPosition);
  }, [editingTaskId, tasks]);
  
  // Handle caret positioning after tab navigation
  useLayoutEffect(() => {
    if (tabCaretPositionRef.current === null) return;
    
    const { taskId, caretPosition } = tabCaretPositionRef.current;
    tabCaretPositionRef.current = null; // Clear after use
    
    const targetElement = document.querySelector(`.task-text[data-react-key="${taskId}"]`);
    if (!targetElement) return;
    
    editingElementRef.current = targetElement;
    
    const targetText = targetElement.textContent || '';
    const targetTextLength = targetText.length;
    const targetCaretPosition = Math.min(caretPosition, targetTextLength);
    
    // Prepare the range before focusing
    const newRange = document.createRange();
    
    if (targetTextLength > 0) {
      const walker = document.createTreeWalker(
        targetElement,
        NodeFilter.SHOW_TEXT,
        null
      );
      
      let currentOffset = 0;
      let textNode = walker.nextNode();
      let targetNode = null;
      let targetOffset = 0;
      
      while (textNode) {
        const nodeLength = textNode.textContent.length;
        if (currentOffset + nodeLength >= targetCaretPosition) {
          targetNode = textNode;
          targetOffset = targetCaretPosition - currentOffset;
          break;
        }
        currentOffset += nodeLength;
        textNode = walker.nextNode();
      }
      
      if (targetNode) {
        newRange.setStart(targetNode, targetOffset);
        newRange.collapse(true);
      } else {
        newRange.selectNodeContents(targetElement);
        newRange.collapse(false);
      }
    } else {
      newRange.setStart(targetElement, 0);
      newRange.collapse(true);
    }
    
    targetElement.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(newRange);
    
  }, [editingTaskId, tasks, editingElementRef]);

  return {
    clickCaretPositionRef,
    arrowKeyCaretPositionRef,
    tabCaretPositionRef
  };
}
